import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { captureFocusReturn } from "../lib/focusReturn";
import { hostLabel } from "../lib/hostLabel";
import { GoonlordsWordmark } from "../control/ui/Logo";
import { MASK } from "../lib/replayMask";

/**
 * The big screen-code card: the code, readable across a living room, over whatever the
 * page is showing.
 *
 * EXTRACTED FROM PendingScreen, because the code is not a /screen thing any more — the
 * site bar's "Screen code" bubble opens this card on every page. It never opens on its
 * own (2026-10-21): only the bubble, or an explicit `?code=1` link, opens it — on a first
 * visit the surface's intro greets you instead.
 *
 * THE CARD, NOT A BLANKET. The full-bleed layer POSITIONS the card and nothing else — no
 * tint, no blur, and transparent to the pointer, so everything it spans stays clickable.
 * A layer that covers the screen invisibly is still covering the screen. The card itself
 * is sized by its contents (`w-fit`), capped by the viewport: a fixed width left a band
 * of empty black either side of a four-character code, which is a lot of television to
 * spend on nothing.
 *
 * `becomeScreen` is the self-serve door: the same tab flipping ITSELF into a screen, no
 * code involved, because this browser already has the account. It used to be a touch-only
 * button squeezed into TopNav; the card has the room to offer it to everybody, and it is
 * the natural place — the person staring at a pairing code is exactly the person deciding
 * which device becomes the screen. Absent on /screen, which is already one.
 */
export function CodeCard({
  code,
  error = false,
  onClose,
  becomeScreen,
}: {
  code: string | null;
  /** True while the server cannot be reached; the card says so instead of a dead code. */
  error?: boolean;
  onClose: () => void;
  becomeScreen?: { busy: boolean; onGo: () => void } | null;
}) {
  // The address to read out — one URL, spoken across a room, which is why it is /remote.
  const host = hostLabel();
  /*
   * FOCUS GOES BACK TO WHOEVER OPENED IT — the bar's code chip, now that the chip is the
   * card's only everyday door (the !137 rule, captureFocusReturn). Captured at mount, so a
   * card opened by a link (`?code=1`, nothing focused) restores nothing. Esc closes it like
   * the ×. NOT a trap: the card is deliberately non-modal (the layer passes the pointer
   * through), so Tab keeps walking the page.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const restoreFocus = captureFocusReturn();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      restoreFocus();
    };
  }, []);
  /*
   * A PORTAL, not a child. `fixed` + a big z-index is not enough: whoever opens this card
   * renders it inside their own subtree, and any ancestor that establishes a stacking
   * context (the landing's `relative z-10` hero chrome, a transformed container) caps the
   * card's z-index at the ancestor's level — measured on the home page, where the card
   * drew UNDER the hero text. Portalling to document.body means no ancestor exists to
   * lose that contest.
   */
  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="pointer-events-auto relative flex max-h-[92dvh] w-fit max-w-[92vw] flex-col items-center justify-center gap-6 overflow-y-auto rounded-3xl border border-white/15 bg-black/65 px-6 py-10 text-center shadow-2xl backdrop-blur-xl sm:gap-8 sm:px-12 sm:py-14">
        {/* Corner ×, away from the code. Big target: this may be a TV, and whoever
            reaches for it may be doing so with a trackpad from the sofa. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Hide the screen code"
          className="absolute right-5 top-5 rounded-full border border-white/15 bg-black/40 px-4 py-2 text-xl leading-none text-zinc-300 transition-colors hover:bg-black/70 hover:text-white"
        >
          ✕
        </button>

        {/* The brand, the same wordmark the bar wears — this popup is the app's face on
            a television somebody else set up. */}
        <GoonlordsWordmark height={26} />

        {error && !code ? (
          <span className="text-2xl text-zinc-400">Can&rsquo;t reach the server — retrying…</span>
        ) : (
          <>
            <span className="text-[clamp(1rem,2.2vw,2.25rem)] text-zinc-300">
              Enter this code on your remote
            </span>

            {/* Sized against the VIEWPORT, not a breakpoint: Tailwind's largest step is a
                caption on a 55" television, and `sm:` cannot tell a laptop from a wall.
                `leading-none` because at this size the default line box adds a hand's
                width of empty space above and below. */}
            {/* A live pairing code: masked in session replay (lib/replayMask), like the
                bar's bubble. The instructions around it stay readable. */}
            <span className="font-mono text-[clamp(3.5rem,12vw,18rem)] font-bold leading-none tracking-[0.15em] text-white" {...MASK}>
              {code ?? "····"}
            </span>

            {/* Where the box IS. Without this the code has no named destination and the
                person holding the phone goes hunting through the library for one. */}
            <span className="max-w-[min(48rem,90vw)] text-[clamp(0.875rem,1.6vw,1.5rem)] leading-relaxed text-zinc-400">
              On your phone, <span className="text-zinc-200">{host}/remote</span>
            </span>

            {becomeScreen ? (
              <button
                type="button"
                disabled={becomeScreen.busy}
                onClick={becomeScreen.onGo}
                className="rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-medium text-zinc-200 transition-colors hover:bg-white/15 hover:text-white disabled:opacity-60"
              >
                {becomeScreen.busy ? "Opening…" : "Or use this device as the screen"}
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
